import type { Environment } from 'types/environment'
import type { Cart, CartToken, ShippingMethod, ShippingMethodName } from 'types/models/cart.model'
import type { AddProduct } from '~/contracts/CartRepositoryContract'
import { CART_TOKENS } from 'configuration/global.configuration'
import consola from 'consola'
import { TWO_DAYS_IN_SECONDS } from 'data/time/milliseconds'
import {
  getByWarehouseRegionCode,
  isInTimeLimit,
  patchByWarehouseRegionCode,
} from 'lib/localStorage'
import { useLastRawCart } from '~/composables/useLastRawCart'
import { getItemsCount } from '~/models/cart/getItemsCount'

export type CartState = Omit<Cart, 'discountCode'>

const DEFAULT_STATE = {
  items: [],
  priceTax: 0,
  priceTotal: 0,
  priceShipping: 0,
  priceProducts: 0,
  priceDiscount: 0,
  itemsCount: 0,
  messages: [],
  countryCode: undefined,
  isFreeShippingDiscountCodeApplied: false,
}

export const useCartStore = defineStore('CartStore', () => {
  const state = ref<CartState>(DEFAULT_STATE)
  const shippingMethods = ref<ShippingMethod[]>([])

  const loading = ref(false)
  const cartToken = ref<string | undefined>(undefined)

  const storefrontStore = useStorefrontStore()
  const gtm = useGTM()
  const discountCodeStore = useDiscountCodeStore()
  const checkoutStore = useCheckoutStore()
  // TODO: Check why $cart cannot be deconstructed
  const context = useNuxtApp()
  const logger = useServerLogging('cart_store')

  const isCartEmpty = computed(() => state.value.itemsCount === 0)
  const freeShipping = computed(
    () => state.value.isFreeShippingDiscountCodeApplied || state.value.shippingMethod?.price === 0,
  )

  const cartSummary = computed(() => ({
    priceTotal: state.value.priceTotal,
    priceProducts: state.value.priceProducts,
    priceShipping: state.value.priceShipping,
    priceDiscount: state.value.priceDiscount,
    itemsCount: getItemsCount(state.value.items),
  }))

  const removeCartToken = () => {
    const storefrontStore = useStorefrontStore()
    patchByWarehouseRegionCode(storefrontStore.current.warehouse.region, CART_TOKENS, undefined)
    cartToken.value = undefined
  }

  const reset = () => {
    removeCartToken()
    state.value = DEFAULT_STATE
    loading.value = false
  }

  const setCartToken = (jwtToken: string) => {
    patchByWarehouseRegionCode(storefrontStore.current.warehouse.region, CART_TOKENS, {
      jwt: jwtToken,
      date: new Date().toDateString(),
    })

    cartToken.value = jwtToken
  }

  const setCartTokenFromLocalStorage = () => {
    try {
      const currentToken = getByWarehouseRegionCode<CartToken>(
        storefrontStore.current.warehouse.region,
        CART_TOKENS,
      )

      if (!currentToken)
        throw String('Missing cart-token')

      if (isInTimeLimit(currentToken.date, TWO_DAYS_IN_SECONDS)) {
        cartToken.value = currentToken.jwt
      }
      else {
        removeCartToken()
        throw String('Old cart-token')
      }
    }
    catch (error) {
      consola.info(error)
    }
  }

  const updateCart = (cart: Cart) => {
    discountCodeStore.code = cart.discountCode
    state.value.shippingMethod = cart.shippingMethod

    checkoutStore.removeAddressView()

    state.value = {
      ...state.value,
      ...cart,
    }
  }

  const fetchCart = async () => {
    try {
      loading.value = true
      const cart = await context.$cart.fetchCart()

      updateCart(cart)

      return cart
    }
    catch (e: any) {
      if (e.message === 'RESET') {
        const cart = useLastRawCart().lastRawCart.value
        logger.logInfo({
          message: 'Cart token reset',
          code: 'fetchCart_reset',
          data: JSON.stringify({ cartToken: cartToken.value, cart, error: e }),
        })
        removeCartToken()
      }

      logger.logError({
        message: 'Failed to fetch cart',
        code: 'error_fetch_cart',
        data: JSON.stringify({error: e }),

      })  
      return Promise.reject(e)
    }
    finally {
      loading.value = false
    }
  }

  const addItem = async (id: string, quantity?: number) => {
    try {
      const cart = await context.$cart.addProduct({
        id,
        quantity: quantity || 1,
      })

      updateCart(cart)
      gtm.pushCartUpdate()
      return Promise.resolve()
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      loading.value = false
    }
  }

  const addItems = async (products: AddProduct[]) => {
    try {
      const cart = await context.$cart.addProducts(products)

      updateCart(cart)
      gtm.pushCartUpdate()
      return Promise.resolve()
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      loading.value = false
    }
  }

  const removeItem = async (barcode: string) => {
    try {
      const cart = await context.$cart.removeProduct(barcode)
      updateCart(cart)
      gtm.pushCartUpdate()
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      loading.value = false
    }
  }

  const updateItemQty = async (product: AddProduct) => {
    try {
      const cart = await context.$cart.updateProduct(product)

      updateCart(cart)
      gtm.pushCartUpdate()
    }
    catch (e) {
      console.error(e)
      return Promise.reject(e)
    }
  }

  const addShippingMethodToCart = async (id: ShippingMethodName) => {
    try {
      loading.value = true 

      const cart = await context.$cart.addProduct({ id, quantity: 1 })
      state.value.shippingMethod = cart.shippingMethod

      loading.value = false

      updateCart(cart)
    }
    catch (e) {
      return Promise.reject(e)
    }
  }

  const fetchAllShippingMethods = async (countryCode?: Environment.CountryCode) => {
    try {
      loading.value = true
      const { currentMarketCountryCode } = storefrontStore
      const { shipping } = checkoutStore.information.address
      countryCode = countryCode || shipping.country || currentMarketCountryCode

      const _shippingMethods = await context.$cart.listShippingMethods(countryCode)

      if (_shippingMethods.length)
        shippingMethods.value = _shippingMethods

      if (!state.value.shippingMethod?.id && _shippingMethods[0]) {
        const id = _shippingMethods[0].id
        await addShippingMethodToCart(id)
      }
      return Promise.resolve()
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      loading.value = false
    }
  }

  const removeShippingMethodFromCart = async () => {
    try {
      if (state.value.shippingMethod?.id) {
        loading.value = true 

        const cart = await context.$cart.removeProduct(state.value.shippingMethod?.id)
        state.value.shippingMethod = undefined

        loading.value = false

        updateCart(cart)
      }
    }
    catch (e) {
      return Promise.reject(e)
    }
  }
  const updateCountryCode = async (code?: Environment.CountryCode) => {
    if (!cartToken.value)
      return

    loading.value = true
    state.value.shippingMethod = undefined

    let countryObject

    if (code) {
      const market = storefrontStore.allMarkets.find((market) => {
        return market.countryCode === code
      })
      if (market)
        countryObject = { countryCode: code, languageCode: market.language }
    }

    try {
      const cart = await context.$cart.updateCountryCode(countryObject)
      if (cart)
        updateCart(cart)
    }
    catch (error) {
    }
    finally {
      loading.value = false
    }
  }

  // Query in the form of 7333102442632x1,7333102606874x2,7333102608397x3
  // TODO: Add individual checks for every product
  async function restoreCartFromQuery(query: string) {
    const items = query.split(',').reduce((acc, item) => {
      const [id, quantity] = item.split('x')
      if (id && quantity) {
        acc.push({
          id,
          quantity: Number.parseInt(quantity),
        })
      }
      return acc
    }, [] as AddProduct[])

    if (items.length === 0)
      return

    try {
      reset()
      let cart

      for (const item of items) {
        try {
          cart = await context.$cart.addProduct(item)
        }
        catch (_) {
        }
      }

      if (!cart)
        return

      updateCart(cart)
      gtm.pushCartUpdate()
      return Promise.resolve()
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      loading.value = false
    }
  }

  return {
    state,
    cartToken,
    freeShipping,
    cartSummary,
    loading,
    isCartEmpty,
    shippingMethods,
    removeCartToken,
    reset,
    setCartToken,
    setCartTokenFromLocalStorage,
    updateCart,
    fetchCart,
    addItem,
    addItems,
    removeItem,
    updateItemQty,
    addShippingMethodToCart,
    updateCountryCode,
    restoreCartFromQuery,
    removeShippingMethodFromCart,
    fetchAllShippingMethods,
  }
})
